import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Link2, Eraser } from "lucide-react";

const ALLOWED = new Set(["P", "BR", "STRONG", "B", "EM", "I", "U", "S", "H2", "H3", "UL", "OL", "LI", "A", "SPAN", "BLOCKQUOTE"]);

function cleanNode(node) {
  // Depth-first clean: unwrap disallowed tags, strip attributes (keep href on <a>, font-size style on <span>).
  [...node.childNodes].forEach((child) => {
    if (child.nodeType === 1) {
      cleanNode(child);
      const tag = child.tagName;
      if (!ALLOWED.has(tag)) {
        while (child.firstChild) node.insertBefore(child.firstChild, child);
        node.removeChild(child);
        return;
      }
      const keepStyle = tag === "SPAN" ? (child.style.fontSize ? `font-size:${child.style.fontSize}` : "") : "";
      const href = tag === "A" ? child.getAttribute("href") : null;
      [...child.attributes].forEach((a) => child.removeAttribute(a.name));
      if (keepStyle) child.setAttribute("style", keepStyle);
      if (href && /^(https?:|mailto:)/i.test(href)) child.setAttribute("href", href);
    } else if (child.nodeType === 8) {
      node.removeChild(child);
    }
  });
}

function cleanPastedHtml(html) {
  // Parse in an INERT document (no script execution, no image/network loads).
  const doc = new DOMParser().parseFromString(
    html.replace(/<(script|style|meta|link|o:p)[^>]*>[\s\S]*?<\/\1>/gi, ""), "text/html");
  cleanNode(doc.body);
  return doc.body.innerHTML;
}

const SIZE = { sm: "0.875em", md: "1em", lg: "1.25em" };

const BTN = "h-9 min-w-9 px-2 rounded-lg hover:bg-mint-soft text-ink grid place-items-center text-sm font-semibold transition-colors";

export function RichTextEditor({ value, onChange, testId = "rich-editor", minHeight = "20rem" }) {
  const ref = useRef(null);
  useEffect(() => {
    try { document.execCommand("defaultParagraphSeparator", false, "p"); } catch (e) { /* noop */ }
    if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const emit = () => onChange(ref.current.innerHTML);
  const cmd = (c, v) => { ref.current.focus(); document.execCommand(c, false, v); emit(); };
  const size = (key) => {
    ref.current.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    if (!ref.current.contains(range.commonAncestorContainer)) return;
    const span = document.createElement("span");
    span.style.fontSize = SIZE[key];
    try { range.surroundContents(span); }
    catch (e) { span.appendChild(range.extractContents()); range.insertNode(span); }
    // Move the caret just after the sized span so subsequent typing is not sized.
    const marker = document.createTextNode("\u200b");
    span.parentNode.insertBefore(marker, span.nextSibling);
    const after = document.createRange();
    after.setStartAfter(marker); after.collapse(true);
    sel.removeAllRanges(); sel.addRange(after);
    emit();
  };
  const block = (tag) => { ref.current.focus(); document.execCommand("formatBlock", false, tag); emit(); };
  const link = () => {
    const url = window.prompt("Link-Adresse (https:// oder mailto:)");
    if (url && /^(https?:|mailto:)/i.test(url)) cmd("createLink", url);
    else if (url) window.alert("Bitte eine gültige Adresse mit https:// oder mailto: eingeben.");
  };
  const clear = () => { ref.current.focus(); document.execCommand("removeFormat"); document.execCommand("formatBlock", false, "P"); emit(); };
  const onPaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    if (html) document.execCommand("insertHTML", false, cleanPastedHtml(html));
    else document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
    emit();
  };

  return (
    <div className="w-card overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-line bg-gray-50">
        <button type="button" className={BTN} title="Fett" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("bold")} data-testid="rt-bold"><Bold size={16} aria-hidden="true" /></button>
        <button type="button" className={BTN} title="Kursiv" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("italic")} data-testid="rt-italic"><Italic size={16} aria-hidden="true" /></button>
        <button type="button" className={BTN} title="Unterstrichen" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("underline")} data-testid="rt-underline"><Underline size={16} aria-hidden="true" /></button>
        <span className="w-px h-6 bg-line mx-1" />
        <button type="button" className={BTN} title="Überschrift" onMouseDown={(e) => e.preventDefault()} onClick={() => block("H2")} data-testid="rt-h2"><Heading2 size={16} aria-hidden="true" /></button>
        <button type="button" className={BTN} title="Unterüberschrift" onMouseDown={(e) => e.preventDefault()} onClick={() => block("H3")} data-testid="rt-h3"><Heading3 size={16} aria-hidden="true" /></button>
        <span className="w-px h-6 bg-line mx-1" />
        <button type="button" className={BTN} title="Aufzählung" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("insertUnorderedList")} data-testid="rt-ul"><List size={16} aria-hidden="true" /></button>
        <button type="button" className={BTN} title="Nummerierte Liste" onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("insertOrderedList")} data-testid="rt-ol"><ListOrdered size={16} aria-hidden="true" /></button>
        <span className="w-px h-6 bg-line mx-1" />
        <button type="button" className={BTN} title="Klein" onMouseDown={(e) => e.preventDefault()} onClick={() => size("sm")} data-testid="rt-size-sm">A<span className="text-[10px]">-</span></button>
        <button type="button" className={BTN} title="Normal" onMouseDown={(e) => e.preventDefault()} onClick={() => size("md")} data-testid="rt-size-md">A</button>
        <button type="button" className={BTN} title="Groß" onMouseDown={(e) => e.preventDefault()} onClick={() => size("lg")} data-testid="rt-size-lg">A<span className="text-[13px]">+</span></button>
        <span className="w-px h-6 bg-line mx-1" />
        <button type="button" className={BTN} title="Link" onMouseDown={(e) => e.preventDefault()} onClick={link} data-testid="rt-link"><Link2 size={16} aria-hidden="true" /></button>
        <button type="button" className={BTN} title="Formatierung entfernen" onMouseDown={(e) => e.preventDefault()} onClick={clear} data-testid="rt-clear"><Eraser size={16} aria-hidden="true" /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onPaste={onPaste}
        className="rt-content p-4 outline-none overflow-y-auto"
        style={{ minHeight }}
        data-testid={`${testId}-area`}
      />
    </div>
  );
}

export function RichHtml({ html, className = "" }) {
  const isHtml = html && /<[a-zA-Z/][^>]*>/.test(html);
  if (isHtml) {
    return <div className={`rt-content ${className}`} dangerouslySetInnerHTML={{ __html: html }} data-testid="rich-html" />;
  }
  return (
    <div className={`rt-content space-y-3 ${className}`} data-testid="rich-html">
      {(html || "").split(/\n{2,}/).map((para, i) =>
        para.startsWith("## ")
          ? <h2 key={i}>{para.replace(/^##\s*/, "")}</h2>
          : <p key={i} className="whitespace-pre-wrap">{para}</p>
      )}
    </div>
  );
}
